import { consoleLog, redirectProfileSlug } from "./helpers"
import { getPlaceDetails } from "../services/placesConnector"
import { getEntitiesData } from "../services/chaperonConnector"
import {
    LOCALSTORAGE_PLACE,
    LOCALSTORAGE_ENTITYMAP,
    SESSIONSTORAGE_ENTITY,
} from "../const/storage"
import { TOAST_ENTITY_UNKNOWN } from "../const/strings_en"

/**
 * Handles place selection from search suggestions
 * @param {string} placeId - Google Place ID
 * @param {Object} vicinityData - Context data with place, setPlace, countryCode, etc.
 * @param {Function} setToastMessage - Function to set toast message
 * @param {Function} setToastOptions - Function to set toast options
 * @param {Function} setShowToast - Function to show/hide toast
 * @param {Object} toastOptions - Current toast options
 * @param {Function} setSearchInput - Function to update search input
 * @param {Function} setSuggestions - Function to update suggestions
 * @param {Object} navigate - React Router navigate function
 * @param {boolean} showToast - Whether to show toast notifications (default: true)
 * @returns {Promise<void>}
 */
export const handlePlaceSelect = async ({
    placeId,
    vicinityData,
    setToastMessage,
    setToastOptions,
    setShowToast,
    toastOptions,
    setSearchInput,
    setSuggestions,
    navigate,
    showToast = true,
}) => {
    if (showToast) {
        setToastMessage(`Analyzing global data...`)
        setToastOptions({ ...toastOptions, icon: "border" })
        setShowToast(true)
    }

    let entityMapping = []
    entityMapping = JSON.parse(
        localStorage.getItem(`${LOCALSTORAGE_ENTITYMAP}`)
    )
    let placeData = JSON.parse(
        sessionStorage.getItem(`${SESSIONSTORAGE_ENTITY}-${placeId}`)
    )
    if (placeData?.id) {
        consoleLog("Place details found in session storage.")
        consoleLog("Pushing mapping from local storage now.")

        if (!Array.isArray(entityMapping)) {
            entityMapping = []
        }
        entityMapping.push({
            id: placeData.id,
            name: placeData.name,
            reference: placeData.fromGoogle.reference,
        })
    } else {
        let found = entityMapping?.find(
            (mapping) => mapping.reference === placeId
        )
        if (found) {
            placeData = JSON.parse(
                sessionStorage.getItem(
                    `${SESSIONSTORAGE_ENTITY}-${found.reference}`
                )
            )
            if (placeData?.id === found.id) {
                consoleLog("Place found via mapping in local storage mapping.")
                return // Note: No need to continue flow
            }
        }

        const placeDetails = await getPlaceDetails(placeId)

        // NOTE:
        // This is the place details object returned by Google Places API
        // The place name may not match the search input name
        // although it will likely match the geographic location (e.g. the capital city)
        if (placeDetails) {
            setSearchInput(placeDetails.formatted_address || placeDetails.name)
            setSuggestions([])
            consoleLog("Selected place:", placeDetails)
            // TODO: Fetch news data for selected place
            // TODO: Filter out news items based on risk data (e.g. score)
            let countryComponent = placeDetails.address_components.find(
                (component) => component.types.includes("country")
            )
            let addressCountry = countryComponent
                ? countryComponent.long_name
                : null

            if (!addressCountry) {
                console.error(
                    "Could not derive country from place details:",
                    placeDetails
                )
                setToastMessage(
                    `We couldn't crunch enough safety data for this location: ${placeDetails.name}` +
                        ` - ${placeDetails?.editorial_summary?.overview || ""}`
                )
                setToastOptions({ ...toastOptions, icon: "border" })
                setShowToast(true)
                return
            }

            // consoleLog("Place details name:", placeDetails.name)
            // consoleLog("Derived Address country code:", addressCountry)
            let placeDataArray = await getEntitiesData(
                placeDetails.name,
                addressCountry,
                placeDetails.formatted_address
            )
            consoleLog("Place data array:", placeDataArray)
            placeData = placeDataArray[0]
            if (placeDataArray.length) {
                placeData.fromGoogle = placeDetails

                consoleLog("Place entity:", placeData)
                localStorage.setItem(
                    LOCALSTORAGE_PLACE,
                    JSON.stringify(placeData)
                )
                sessionStorage.setItem(
                    `${SESSIONSTORAGE_ENTITY}-${placeId}`,
                    JSON.stringify(placeData)
                )
            }
        } else {
            console.error("No place details found for placeId:", placeId)
            setToastMessage(TOAST_ENTITY_UNKNOWN)
            setShowToast(true)
        }
    }

    consoleLog("Entity Mapping Array from storage:", entityMapping)

    consoleLog("Pushing mapping now.")
    if (!Array.isArray(entityMapping)) {
        entityMapping = []
    }
    entityMapping.push({
        id: placeData.id,
        name: placeData.name,
        slug: placeData.slug,
        reference: placeData.fromGoogle.reference,
    })

    // Remove duplicates from entityMapping
    entityMapping = entityMapping.filter(
        (mapping, index, self) =>
            index === self.findIndex((m) => m.id === mapping.id)
    )

    localStorage.setItem(
        `${LOCALSTORAGE_ENTITYMAP}`,
        JSON.stringify(entityMapping)
    )

    consoleLog("Final Entity Mapping Array:", entityMapping)
    consoleLog("Setting place now:", placeData)

    vicinityData.setPlace(placeData)

    setSearchInput("")
    const searchInputBox = document.getElementById("searchInputBox")
    if (searchInputBox) searchInputBox.placeholder = placeData.name
    vicinityData.setCountryCode(placeData.country_code)
    setShowToast(false)

    let pathParts = window.location.pathname.split("/")
    if (pathParts[1] === "analysis" && pathParts[2]) {
        redirectProfileSlug(placeData, navigate)
    }
}
